import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';
import toast from 'react-hot-toast';

interface GiftItem {
  id: number;
  gift_name: string;
  type: string;
  amount: number;
  status: string;
  gift_image: string | null;
  delivery_name: string | null;
  delivery_phone: string | null;
  delivery_address: string | null;
  delivery_status: string | null;
  tracking_number: string | null;
}

interface AddressItem {
  id: number;
  name: string;
  phone: string;
  full_address: string;
  is_default: boolean;
}

export default function GiftPage() {
  const navigate = useNavigate();
  const [gifts, setGifts] = useState<GiftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGift, setSelectedGift] = useState<GiftItem | null>(null);
  const [addresses, setAddresses] = useState<AddressItem[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', phone: '', address: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/reward-gifts').then(({ data }) => {
      setGifts(data.data ?? []);
    }).catch(() => toast.error('加载失败')).finally(() => setLoading(false));
  }, []);

  function openModal(gift: GiftItem) {
    setSelectedGift(gift);
    setShowNewForm(false);
    setSelectedAddressId(null);
    setNewForm({ name: '', phone: '', address: '' });
    api.get('/addresses').then(({ data }) => {
      const list: AddressItem[] = data?.addresses ?? [];
      setAddresses(list);
      const def = list.find(a => a.is_default);
      if (def) setSelectedAddressId(def.id);
      else if (list.length > 0) setSelectedAddressId(list[0].id);
      else setShowNewForm(true);
    }).catch(() => { setAddresses([]); setShowNewForm(true); });
  }

  async function submitDelivery() {
    if (!selectedGift) return;

    let name: string, phone: string, address: string;

    if (showNewForm) {
      if (!newForm.name || !newForm.phone || !newForm.address) {
        toast.error('请填写完整收货信息');
        return;
      }
      name = newForm.name;
      phone = newForm.phone;
      address = newForm.address;
    } else {
      const addr = addresses.find(a => a.id === selectedAddressId);
      if (!addr) {
        toast.error('请选择收货地址');
        return;
      }
      name = addr.name;
      phone = addr.phone;
      address = addr.full_address;
    }

    setSubmitting(true);
    try {
      if (showNewForm) {
        await api.post('/addresses', {
          name: newForm.name,
          phone: newForm.phone,
          address: newForm.address,
          is_default: addresses.length === 0,
        });
      }

      await api.post(`/reward-gifts/${selectedGift.id}/delivery-info`, { name, phone, address });
      toast.success('收货信息提交成功');
      setGifts(prev => prev.map(g => g.id === selectedGift.id ? {
        ...g,
        delivery_name: name,
        delivery_phone: phone,
        delivery_address: address,
        delivery_status: 'pending',
      } : g));
      setSelectedGift(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? '提交失败');
    } finally {
      setSubmitting(false);
    }
  }

  function statusLabel(gift: GiftItem) {
    if (gift.delivery_status === 'completed') return '已签收';
    if (gift.delivery_status === 'shipped') return '已发货';
    if (gift.delivery_name) return '待发货';
    return '待领取';
  }

  function statusColor(gift: GiftItem) {
    if (gift.delivery_status === 'completed') return 'bg-green-500';
    if (gift.delivery_status === 'shipped') return 'bg-blue-500';
    if (gift.delivery_name) return 'bg-yellow-500';
    return 'bg-[#c4161c]';
  }

  return (
    <div className="max-w-md mx-auto min-h-screen pb-6" style={{ backgroundColor: '#8b0000' }}>
      <AuthGuard />

      {/* Header */}
      <div className="relative px-4 pt-4 pb-3">
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 text-black"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <h1 className="text-center text-white font-bold text-lg">我的奖品</h1>
      </div>

      {/* Gift list */}
      <div className="px-4 space-y-3">
        {loading && <p className="text-center text-white/60 py-10">加载中...</p>}
        {!loading && gifts.length === 0 && (
          <div className="text-center text-white/60 py-10">
            <p className="text-base">暂无奖品</p>
            <p className="text-xs mt-2">邀请好友实名认证即可获得奖品</p>
          </div>
        )}
        {gifts.map(gift => (
          <div key={gift.id} className="bg-white rounded-xl overflow-hidden">
            <div className="flex items-center p-3 gap-3">
              {gift.gift_image ? (
                <img src={`/images/${gift.gift_image}`} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-[#fef3c7] flex items-center justify-center flex-shrink-0">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c4161c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 12v10H4V12" /><path d="M2 7h20v5H2z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
                  </svg>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-800 truncate">{gift.gift_name}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] text-white px-2 py-0.5 rounded-full ${statusColor(gift)}`}>
                    {statusLabel(gift)}
                  </span>
                  {gift.tracking_number && (
                    <span className="text-[10px] text-gray-500">单号: {gift.tracking_number}</span>
                  )}
                </div>
              </div>
              {gift.type === 'real' && !gift.delivery_name && (
                <button
                  onClick={() => openModal(gift)}
                  className="bg-[#c4161c] text-white text-[11px] font-bold px-3 py-1.5 rounded-lg flex-shrink-0"
                >
                  填写地址
                </button>
              )}
            </div>
            {gift.delivery_name && (
              <div className="border-t border-gray-100 px-3 py-2 text-[11px] text-gray-500">
                <span>{gift.delivery_name} | {gift.delivery_phone}</span>
                <p className="truncate">{gift.delivery_address}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Address selection modal */}
      {selectedGift && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl px-5 py-5 w-[340px] max-h-[80vh] overflow-y-auto">
            <h3 className="text-sm font-bold text-gray-800 mb-1 text-center">选择收货地址</h3>
            <p className="text-xs text-gray-500 mb-3 text-center">{selectedGift.gift_name}</p>

            {!showNewForm && (
              <>
                {addresses.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {addresses.map(addr => (
                      <div
                        key={addr.id}
                        onClick={() => setSelectedAddressId(addr.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer ${
                          selectedAddressId === addr.id ? 'border-[#c4161c] bg-red-50' : 'border-gray-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-800">{addr.name}</span>
                          <span className="text-xs text-gray-500">{addr.phone}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{addr.full_address}</p>
                        {addr.is_default && (
                          <span className="text-[9px] bg-[#c4161c] text-white px-1.5 py-0.5 rounded mt-1 inline-block">默认</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 text-center py-3">暂无保存的地址</p>
                )}
                <button
                  onClick={() => setShowNewForm(true)}
                  className="w-full border border-dashed border-gray-300 rounded-lg py-2 text-xs text-gray-500 mb-3"
                >
                  + 添加新地址
                </button>
              </>
            )}

            {showNewForm && (
              <div className="space-y-3 mb-3">
                {addresses.length > 0 && (
                  <button
                    onClick={() => setShowNewForm(false)}
                    className="text-xs text-[#c4161c] mb-1"
                  >
                    ← 返回选择已有地址
                  </button>
                )}
                <input
                  type="text"
                  placeholder="收件人姓名"
                  value={newForm.name}
                  onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="tel"
                  placeholder="联系电话"
                  value={newForm.phone}
                  onChange={e => setNewForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <textarea
                  placeholder="详细收货地址"
                  value={newForm.address}
                  onChange={e => setNewForm(f => ({ ...f, address: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
            )}

            <div className="flex gap-3 justify-center">
              <button
                className="bg-gray-200 text-gray-700 text-sm px-5 py-2 rounded-lg"
                onClick={() => setSelectedGift(null)}
              >取消</button>
              <button
                className="bg-[#c4161c] text-white text-sm px-5 py-2 rounded-lg disabled:bg-gray-400"
                onClick={submitDelivery}
                disabled={submitting}
              >{submitting ? '提交中...' : '确认提交'}</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
